// src/app/services/payment.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';

export interface PaymentMethod {
  id?: string;
  bankName: string;
  accountHolder: string;
  accountNumber: string;
  ifsc: string;
  upiId?: string;
  paymentLink?: string;
  qrCodeUrl?: string;
  cardId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface PaymentStatus {
  paid: boolean;
  transactionId: string | null;
  amount: number;
  currency: string;
  paymentDate: string | null;
}

export interface PaymentInitiation {
  orderId: string;
  amount: number;
  currency: string;
  paymentGatewayData: {
    key: string;
    orderId: string;
    amount: number;
  };
}

export interface CreatePaymentMethodRequest {
  bankName: string;
  accountHolder: string;
  accountNumber: string;
  ifsc: string;
  upiId?: string;
  paymentLink?: string;
  qrCodeUrl?: string;
}

export interface UpdatePaymentMethodRequest {
  bankName?: string;
  accountHolder?: string;
  accountNumber?: string;
  ifsc?: string;
  upiId?: string;
  paymentLink?: string;
  qrCodeUrl?: string;
}

export interface PaymentVerificationRequest {
  paymentId: string;
  orderId: string;
  signature: string;
}

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  private readonly baseUrl = environment.apiUrl;

  constructor(private http: HttpClient, private authService: AuthService) {}

  private getAuthHeaders(): HttpHeaders {
    const token = this.authService.idToken;
    if (!token) {
      throw new Error('User not authenticated');
    }
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
  }

  /**
   * Initiate payment for a card
   * POST /api/cards/:cardId/payments/initiate
   */
  initiatePayment(cardId: string, amount: number): Observable<PaymentInitiation> {
    return this.http.post<PaymentInitiation>(
      `${this.baseUrl}/cards/${cardId}/payments/initiate`,
      { amount },
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * Verify payment
   * POST /api/cards/:cardId/payments/verify
   */
  verifyPayment(
    cardId: string,
    verification: PaymentVerificationRequest
  ): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/cards/${cardId}/payments/verify`,
      verification,
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * Create new payment method details
   * POST /api/cards/:cardId/payment-methods
   */
  createPaymentDetails(
    cardId: string,
    paymentData: CreatePaymentMethodRequest
  ): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/cards/${cardId}/payment-methods`,
      paymentData,
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * Fetch saved payment method details
   * GET /api/cards/:cardId/payment-methods
   */
  fetchPaymentDetails(cardId: string): Observable<PaymentMethod[]> {
    return this.http.get<PaymentMethod[]>(
      `${this.baseUrl}/cards/${cardId}/payment-methods`,
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * Update existing payment method
   * PUT /api/cards/:cardId/payment-methods
   */
  updatePaymentDetails(
    cardId: string,
    data: UpdatePaymentMethodRequest
  ): Observable<any> {
    return this.http.put(
      `${this.baseUrl}/cards/${cardId}/payment-methods`,
      data,
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * Delete payment method
   * DELETE /api/cards/:cardId/payments
   */
  deletePaymentDetails(methodId: string): Observable<any> {
    return this.http.delete(
      `${this.baseUrl}/cards/${methodId}/payment-methods`,
      { headers: this.getAuthHeaders() }
    );
  }
}